// src/App.tsx
import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom'; // <-- Import useNavigate
import HomePage from './pages/HomePage';
import SettingsPage from './pages/SettingsPage';
import CallbackPage from './pages/CallbackPage';
import PostPage from './pages/PostPage';
import SearchResultsPage from './pages/SearchResultsPage';
import SearchBar from './components/SearchBar';
import SecretTikTokPage from './pages/tiktok';
import ReloadPrompt from './components/ReloadPrompt';

function App() {
  const location = useLocation();
  const navigate = useNavigate(); // <-- Get navigate function
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  // --- PWA Launch Handler ---
  useEffect(() => {
    // Check if the Launch Handler API is available
    if ('launchQueue' in window && window.launchQueue) {
      console.log('Launch Handler API detected.', window.launchQueue);
      window.launchQueue.setConsumer(async (launchParams) => {
        // Check if there are files launched (we don't handle files here)
        if (!launchParams.files || launchParams.files.length === 0) {
          console.log('Launch consumer: No files launched.');
          // Check if launched with a target URL
          if (launchParams.targetURL) {
            const target = new URL(launchParams.targetURL);
            const targetPath = target.pathname + target.search + target.hash;

            console.log(`Launch consumer: Target URL detected: ${launchParams.targetURL}`);
            console.log(`Launch consumer: Navigating internally to: ${targetPath}`);

            // Use react-router's navigate function to go to the internal route
            // This assumes your internal routes match the reddit.com paths (/r/..., /r/.../comments/...)
            // which they do based on your current routing setup.
            navigate(targetPath);
          } else {
             console.log('Launch consumer: No target URL found.');
          }
          return; // Exit if no files
        }
        // Handle file launches if needed in the future
        // for (const fileHandle of launchParams.files) {
        //   // Handle file
        // }
      });
    } else {
       console.warn('Launch Handler API not supported in this browser.');
    }
  }, [navigate]); // Add navigate as a dependency

  // Close search when navigating away
  useEffect(() => {
    setIsSearchOpen(false);
  }, [location.pathname]);

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Calculate header height for main content padding
  useEffect(() => {
    if (headerRef.current) {
      const resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) { setHeaderHeight(entry.target.clientHeight); }
      });
      resizeObserver.observe(headerRef.current);
      setHeaderHeight(headerRef.current.clientHeight);
      return () => resizeObserver.disconnect();
    }
  }, []);

  const isTikTokPage = location.pathname.startsWith('/tiktok');

  return (
    <div className="min-h-screen flex flex-col font-sans">
      {/* Header */}
      {!isTikTokPage && (<header ref={headerRef} className="py-5 top-0 left-0 right-0 z-30 bg-lightbg dark:bg-darkbg">
        {/* ... Header content ... */}
         <div className="content-wrapper flex justify-between items-center gap-4">
            {/* Site Title */}
            <Link
                to="/"
                className="text-xl font-bold text-gray-900 dark:text-white hover:no-underline focus:outline-none focus-visible:ring-0 whitespace-nowrap"
                onClick={() => setIsSearchOpen(false)} // Close search on title click
            >
              Reader
            </Link>

            {/* Right Aligned Nav/Search Area */}
            <div className="flex items-center gap-4 md:gap-5">
                {/* Conditional Search Bar */}
                {isSearchOpen ? (
                    <div className="flex-1 flex justify-end">
                        <SearchBar ref={searchInputRef} onClose={() => setIsSearchOpen(false)} />
                    </div>
                ) : (
                    /* Regular Navigation & Search Toggle */
                    <nav>
                      <ul className="flex items-center space-x-4 md:space-x-5">
                        <li>
                          <Link to="/" className="text-sm text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                            Home
                          </Link>
                        </li>
                        <li>
                          <Link to="/settings" className="text-sm text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                            Settings
                          </Link>
                        </li>
                        {/* Search Toggle Button */}
                        <li>
                            <button
                                onClick={() => setIsSearchOpen(true)}
                                className="text-sm text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center"
                                aria-label="Open search"
                                title="Search"
                            >
                                {/* Search Icon */}
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </button>
                        </li>
                      </ul>
                    </nav>
                )}
            </div>
          </div>
      </header>)}

      {/* Main Content Area */}
      <main className="flex-1 py-8 md:py-12" style={{ paddingTop: `${headerHeight + 32}px` }}>
        <Routes>
          <Route path="/search" element={<SearchResultsPage />} />
          {/* Ensure your post route captures the full potential path */}
          <Route path="/r/:subredditName/comments/:postId/*" element={<PostPage />} />
          <Route path="/r/:subredditName" element={<HomePage />} />
          <Route path="/" element={<HomePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/tiktok" element={<SecretTikTokPage />} />
          <Route path="/tiktok/:subredditName" element={<SecretTikTokPage />} />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="py-6 mt-16 border-t border-gray-200 dark:border-gray-700">
        {/* ... Footer content ... */}
        <div className="content-wrapper text-center text-xs text-gray-500 dark:text-gray-400">
                Minimal Reddit Reader
        </div>
      </footer>

      {/* PWA Reload Prompt */}
      <ReloadPrompt />
    </div>
  );
}

export default App;